import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useInventoryList, useInventorySyncStatus, inventoryKeys } from '../hooks/useInventory';
import { useTagColors, useInventoryTags } from '../hooks/useTagColors';
import { useAiAttention } from '../hooks/useAi';
import { useLionAutoStatus } from '../hooks/useLionAuto';
import { updatePrice } from '../api';
import { describePriceError, priceLimitsHint } from '../utils/priceErrors';
import { formatDateTimeEastern, formatEventDateTime, formatTimeEastern } from '../utils/dates';
import { openLionAuto, buildLionAutoUrl } from '../utils/compareLink';
import { autoBadgesByEvent } from '../utils/lionAutoRunner';
import { isAdminLike, isStaff, isSuperAdmin } from '../utils/roles';
import { safeUrl } from '../utils/safeUrl';
import AttentionPanel from '../components/AttentionPanel';
import DashboardFilters, { EMPTY_FILTERS } from '../components/DashboardFilters';

const STALE_MS = 48 * 60 * 60 * 1000;
const isStaleItem = (item) =>
  !item.lastPriceUpdate || (Date.now() - new Date(item.lastPriceUpdate).getTime()) > STALE_MS;

// The Lion Auto badge on an event header (autoBadgesByEvent); a click opens
// the event's Lion Auto page in a new tab, as Compare does
function AutoBadge({ badge, event }) {
  if (!badge) return null;
  return (
    <button
      type="button"
      className={`la-auto-chip ${badge.kind}`}
      title={badge.title}
      onClick={() => window.open(buildLionAutoUrl(event), '_blank')}
    >
      {badge.text}
    </button>
  );
}

function Dashboard({ user, onLogout }) {
  const [editingPrices, setEditingPrices] = useState({});
  const [savingPrices, setSavingPrices] = useState({});
  // Rejected writes keyed String(inventoryId): the typed value stays in the box
  // with a NOT SAVED marker instead of a toast that scrolls past (same rule as
  // Compare — a refused write used to look like a successful one).
  const [rowErrors, setRowErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 250;

  // Search filters (draft state lives in DashboardFilters; only applied
  // values are kept here so typing doesn't re-render the event list)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  // Triage quick filters: 'all' | 'attention' | 'stale' | 'unlisted' | 'auto'.
  // All but 'auto' work over the loaded page; 'auto' asks the server for every
  // Auto event (eventIds below), paged as usual.
  const [quickFilter, setQuickFilter] = useState('all');

  // Events with a Lion Auto group on Auto (or one the runner paused): the
  // badge on the event header and the "Auto on" quick filter. Staff only (a
  // pricer gets only the events they can price); re-read every 30 s.
  const { data: autoStatus } = useLionAutoStatus({ enabled: isStaff(user.role), poll: true });
  const autoBadges = useMemo(() => autoBadgesByEvent(autoStatus), [autoStatus]);

  // Build clean filters for query
  const cleanFilters = { page: currentPage, limit: PAGE_SIZE };
  // Sorted, so the list's query key only changes when the set of Auto events does
  if (quickFilter === 'auto') cleanFilters.eventIds = [...autoBadges.keys()].sort();
  if (appliedFilters.dateFrom) cleanFilters.dateFrom = appliedFilters.dateFrom;
  if (appliedFilters.dateTo) cleanFilters.dateTo = appliedFilters.dateTo;
  if (appliedFilters.performer) cleanFilters.performer = appliedFilters.performer;
  if (appliedFilters.venue) cleanFilters.venue = appliedFilters.venue;
  if (appliedFilters.tags?.length) cleanFilters.tags = appliedFilters.tags;
  if (appliedFilters.antiTags?.length) cleanFilters.antiTags = appliedFilters.antiTags;
  if (appliedFilters.listed === 'listed') cleanFilters.listed = 'true';
  if (appliedFilters.listed === 'unlisted') cleanFilters.listed = 'false';
  if (appliedFilters.purchaseDateFrom) cleanFilters.purchaseDateFrom = appliedFilters.purchaseDateFrom;
  if (appliedFilters.purchaseDateTo) cleanFilters.purchaseDateTo = appliedFilters.purchaseDateTo;

  const {
    data: inventory,
    isLoading: loading,
    isFetching: fetching,
    error: queryError,
  } = useInventoryList(cleanFilters);

  // Server cache freshness (polled every 15 s; refetches the list when the
  // cache version changed). Drives the "as of" label.
  const { data: sync } = useInventorySyncStatus();
  const syncPaused = !!sync && (!!sync.degraded || (sync.syncedAgoSeconds ?? 0) > 120);

  const { data: tagColors = {} } = useTagColors();
  const { data: inventoryTags = [] } = useInventoryTags();

  const { data: attentionData } = useAiAttention();
  const attentionEventIds = useMemo(
    () => new Set((attentionData?.flags || []).map(f => String(f.eventId))),
    [attentionData]
  );

  const events = useMemo(() => inventory?.events || [], [inventory]);

  const triage = useMemo(() => {
    let items = 0, stale = 0, unlisted = 0, attentionEvents = 0;
    for (const ev of events) {
      if (attentionEventIds.has(String(ev.eventId))) attentionEvents++;
      for (const item of ev.inventory) {
        items++;
        if (isStaleItem(item)) stale++;
        if (!item.listed) unlisted++;
      }
    }
    return { items, stale, unlisted, attentionEvents };
  }, [events, attentionEventIds]);

  const visibleEvents = useMemo(() => {
    if (quickFilter === 'attention') {
      return events.filter(ev => attentionEventIds.has(String(ev.eventId)));
    }
    if (quickFilter === 'auto') {
      // The server already sent only Auto events; this drops one that left
      // the set while the refetch is in flight
      return events.filter(ev => autoBadges.has(String(ev.eventId)));
    }
    if (quickFilter === 'stale') {
      return events
        .map(ev => ({ ...ev, inventory: ev.inventory.filter(isStaleItem) }))
        .filter(ev => ev.inventory.length > 0);
    }
    if (quickFilter === 'unlisted') {
      return events
        .map(ev => ({ ...ev, inventory: ev.inventory.filter(i => !i.listed) }))
        .filter(ev => ev.inventory.length > 0);
    }
    return events;
  }, [events, quickFilter, attentionEventIds, autoBadges]);

  const columnCount = 9 + (user.canSeeCost ? 1 : 0);

  // What the server checks this user's writes against (null = unlimited)
  const limitsHint = priceLimitsHint(user);

  const applySearch = useCallback((f) => {
    setCurrentPage(0);
    setAppliedFilters(f);
  }, []);

  const applyClear = useCallback(() => {
    setCurrentPage(0);
    setAppliedFilters(EMPTY_FILTERS);
  }, []);

  // Going into or out of 'auto' swaps the server's list, so start at page 1
  const handleQuickFilter = (next) => {
    if ((next === 'auto') !== (quickFilter === 'auto')) setCurrentPage(0);
    setQuickFilter(next);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearRowError = (inventoryId) => {
    setRowErrors(prev => {
      const key = String(inventoryId);
      if (prev[key] === undefined) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handlePriceChange = (inventoryId, value) => {
    clearRowError(inventoryId); // a new number is a new attempt
    setEditingPrices(prev => ({
      ...prev,
      [inventoryId]: value
    }));
  };

  const handlePriceSave = async (inventoryId, currentPrice, { fromBlur = false } = {}) => {
    const newPrice = parseFloat(editingPrices[inventoryId]);

    if (isNaN(newPrice) || newPrice < 0) {
      setError('Invalid price');
      return;
    }

    // The input keeps focus after a rejection, so every blur would re-send the
    // same price the server already refused. Only an explicit Enter/SET retries.
    const failed = rowErrors[String(inventoryId)];
    if (fromBlur && failed && failed.attemptedPrice === newPrice) return;

    if (newPrice === currentPrice) {
      setEditingPrices(prev => {
        const updated = { ...prev };
        delete updated[inventoryId];
        return updated;
      });
      return;
    }

    try {
      setSavingPrices(prev => ({ ...prev, [inventoryId]: true }));
      clearRowError(inventoryId); // retrying: drop the previous rejection
      // Stale-price guard: the server 409s if this row's B moved since we read it
      await updatePrice(inventoryId, newPrice, { expectedCurrentPrice: Number(currentPrice) || 0 });

      // Optimistic update in cache (L Price is only shown on Compare, where
      // Skybox returns the real value - don't fabricate one here)
      queryClient.setQueryData(
        inventoryKeys.list(cleanFilters),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            events: old.events.map(event => ({
              ...event,
              inventory: event.inventory.map(item =>
                item.id === inventoryId
                  ? { ...item, bPrice: newPrice, listPrice: newPrice, lastPriceUpdate: new Date().toISOString() }
                  : item
              )
            }))
          };
        }
      );

      // Mark all inventory queries stale so other filter/page caches refetch
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all, refetchType: 'none' });

      setEditingPrices(prev => {
        const updated = { ...prev };
        delete updated[inventoryId];
        return updated;
      });

      setSuccessMessage('Price updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      const info = describePriceError(err);
      setRowErrors(prev => ({ ...prev, [String(inventoryId)]: { ...info, attemptedPrice: newPrice } }));
      setError(info.message);
      setTimeout(() => setError(''), 6000);
    } finally {
      setSavingPrices(prev => ({ ...prev, [inventoryId]: false }));
    }
  };

  const handleKeyPress = (e, inventoryId, currentPrice) => {
    if (e.key === 'Enter') {
      handlePriceSave(inventoryId, currentPrice);
    } else if (e.key === 'Escape') {
      clearRowError(inventoryId); // abandoning the edit retires its rejection
      setEditingPrices(prev => {
        const updated = { ...prev };
        delete updated[inventoryId];
        return updated;
      });
    }
  };

  const openMarketView = (url) => {
    const safe = safeUrl(url);
    if (safe) {
      window.open(safe, '_blank', 'noopener');
    }
  };

  // The Compare button opens the event's Lion Auto page in a new tab (URL builder shared in utils/compareLink)
  const openPricingTool = (event) => openLionAuto(event);

  // Event dates are venue-local wall clock — display verbatim, no tz conversion
  const formatDate = (dateStr) => formatEventDateTime(dateStr);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="app-container">
      <nav className="navbar">
        <div className="navbar-brand">Pricing Portal</div>
        <div className="navbar-user">
          <span>{user.displayName} ({user.role})</span>
          {isAdminLike(user.role) && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin')}>
              Admin Panel
            </button>
          )}
          {isSuperAdmin(user.role) && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/reports')}>
              Reports
            </button>
          )}
          {user.role === 'pricer' && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/my-artists')}>
              My Artists
            </button>
          )}
          {isStaff(user.role) && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/orders')}>
              Note Requests
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        {(error || queryError) && (
          <div className="message message-error">
            {error || queryError?.message || 'Failed to load inventory'}
          </div>
        )}
        {successMessage && <div className="message message-success">{successMessage}</div>}

        {/* AI "needs attention" list — renders nothing when the assistant is off */}
        <AttentionPanel />

        {/* Worklist layout: filter rail left, triage chips + flat listings grid right */}
        <div className="dashboard-split">
          <aside className="dashboard-filter-rail">
            <DashboardFilters
              inventoryTags={inventoryTags}
              onSearch={applySearch}
              onClear={applyClear}
            />
          </aside>

          <div className="dashboard-main-col">
            <div className="triage-bar">
              <button
                className={`triage-chip${quickFilter === 'all' ? ' active' : ''}`}
                onClick={() => handleQuickFilter('all')}
              >
                All <span className="triage-n">{triage.items}</span>
              </button>
              <button
                className={`triage-chip danger${quickFilter === 'attention' ? ' active' : ''}`}
                onClick={() => handleQuickFilter('attention')}
                title="Events flagged by the AI assistant (on this page)"
              >
                Needs attention <span className="triage-n">{triage.attentionEvents} events</span>
              </button>
              <button
                className={`triage-chip warn${quickFilter === 'stale' ? ' active' : ''}`}
                onClick={() => handleQuickFilter('stale')}
                title="Listings with no price update in the last 48 hours"
              >
                Stale &gt;48h <span className="triage-n">{triage.stale}</span>
              </button>
              <button
                className={`triage-chip${quickFilter === 'unlisted' ? ' active' : ''}`}
                onClick={() => handleQuickFilter('unlisted')}
              >
                Unlisted <span className="triage-n">{triage.unlisted}</span>
              </button>
              {isStaff(user.role) && (
                <button
                  className={`triage-chip ok${quickFilter === 'auto' ? ' active' : ''}`}
                  onClick={() => handleQuickFilter('auto')}
                  title="Every event with a Lion Auto group on Auto, or one the runner paused, across all pages. An event with no open listings (or outside your filters) counts here but is not listed. The badge on each event header says whether the runner is live, in dry run or switched off."
                >
                  Auto on <span className="triage-n">{autoBadges.size} events</span>
                </button>
              )}
              {inventory?.totalEvents > PAGE_SIZE && (
                <span className="triage-pager">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage + 1} of {Math.ceil(inventory.totalEvents / PAGE_SIZE)}
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!inventory.hasMore}
                  >
                    Next
                  </button>
                </span>
              )}
              <span className="triage-stats">
                {inventory?.totalEvents || 0} events &middot; {inventory?.totalInventory || 0} items
                {sync?.asOf && (
                  <>
                    {' '}&middot;{' '}
                    <span
                      className={`triage-sync${syncPaused ? ' warn' : ''}`}
                      title={syncPaused
                        ? `Skybox sync paused (${sync.degraded || 'no update for ' + sync.syncedAgoSeconds + 's'}); showing the last good data`
                        : 'Listings are synced from Skybox every 15 s; this page reloads within about 30 s of a change'}
                    >
                      {syncPaused ? 'sync paused' : `as of ${formatTimeEastern(sync.asOf)}`}
                    </span>
                    <button
                      type="button"
                      className="triage-sync-btn"
                      onClick={() => queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() })}
                      disabled={fetching}
                      title="Reload listings now"
                    >
                      {fetching ? '…' : '↻'}
                    </button>
                  </>
                )}
              </span>
            </div>

        {loading ? (
          <div className="loading-skeleton-container">
            {/* Skeleton Event Cards */}
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-event-card">
                <div className="skeleton-event-header">
                  <div className="skeleton-event-info">
                    <div className="skeleton skeleton-event-title"></div>
                    <div className="skeleton skeleton-event-date"></div>
                  </div>
                  <div className="skeleton-event-actions">
                    <div className="skeleton skeleton-btn"></div>
                    <div className="skeleton skeleton-btn"></div>
                  </div>
                </div>
                <div className="skeleton-table">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="skeleton-table-row">
                      <div className="skeleton skeleton-table-cell narrow"></div>
                      <div className="skeleton skeleton-table-cell narrow"></div>
                      <div className="skeleton skeleton-table-cell narrow"></div>
                      <div className="skeleton skeleton-table-cell narrow"></div>
                      <div className="skeleton skeleton-table-cell narrow"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : inventory?.events?.length === 0 ? (
          <div className="card">
            <p>{isAdminLike(user.role) ? 'No inventory found. Try adjusting your filters.' : 'No inventory found for your assigned artists.'}</p>
          </div>
        ) : (
          <>
            {visibleEvents.length === 0 ? (
              <div className="card">
                <p>No listings match this quick filter on the current page.</p>
              </div>
            ) : (
            <div className="card worklist-card">
              <div className="table-container">
                <table>
                  <colgroup>
                    <col className="wl-col-section" />
                    <col className="wl-col-row" />
                    <col className="wl-col-seats" />
                    <col className="wl-col-qty" />
                    {user.canSeeCost && <col className="wl-col-cost" />}
                    <col className="wl-col-bprice" />
                    <col className="wl-col-notes" />
                    <col className="wl-col-tags" />
                    <col className="wl-col-updated" />
                    <col className="wl-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>Row</th>
                      <th>Seats</th>
                      <th>Qty</th>
                      {user.canSeeCost && <th>Cost</th>}
                      <th>B Price</th>
                      <th>Public Notes</th>
                      <th>Tags</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEvents.map(event => (
                      <React.Fragment key={event.eventId}>
                        <tr className="worklist-group-row">
                          <td colSpan={columnCount}>
                            <div className="worklist-group">
                              <span className="worklist-group-name">{event.eventName}</span>
                              <AutoBadge badge={autoBadges.get(String(event.eventId))} event={event} />
                              <span className="worklist-group-meta">
                                {formatDate(event.eventDate)} &middot; {event.venue}, {event.venueCity}, {event.venueState} &middot; {event.inventory.length} listing{event.inventory.length === 1 ? '' : 's'}
                              </span>
                              <span className="worklist-group-actions">
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => openPricingTool(event)}
                                >
                                  Compare
                                </button>
                                {event.vividSeatsEventUrl && (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => openMarketView(event.vividSeatsEventUrl)}
                                  >
                                    VividSeats
                                  </button>
                                )}
                                {event.stubhubEventUrl && (
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => openMarketView(event.stubhubEventUrl)}
                                  >
                                    StubHub
                                  </button>
                                )}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {event.inventory.map(item => (
                          <tr key={item.id}>
                            <td>{item.section}</td>
                            <td>{item.row}</td>
                            <td>{item.seatNumbers || `${item.lowSeat}-${item.highSeat}`}</td>
                            <td>{item.quantity}</td>
                            {user.canSeeCost && (
                              <td>{formatCurrency(item.unitCostAverage)}</td>
                            )}
                            <td>
                              <div className="price-cell">
                                {editingPrices[item.id] !== undefined ? (
                                  <>
                                    <input
                                      type="number"
                                      className={`price-input${rowErrors[String(item.id)] ? ' has-error' : ''}`}
                                      title={limitsHint || undefined}
                                      value={editingPrices[item.id]}
                                      onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                      onKeyDown={(e) => handleKeyPress(e, item.id, item.bPrice)}
                                      onBlur={() => handlePriceSave(item.id, item.bPrice, { fromBlur: true })}
                                      autoFocus
                                      step="1"
                                      min="0"
                                    />
                                    {savingPrices[item.id] && <span>...</span>}
                                  </>
                                ) : (
                                  <span
                                    className="editable-price"
                                    onClick={() => handlePriceChange(item.id, (item.bPrice || 0).toString())}
                                  >
                                    {formatCurrency(item.bPrice)}
                                  </span>
                                )}
                              </div>
                              {rowErrors[String(item.id)] && (
                                <div className="price-row-error">
                                  <span className="price-unsaved-chip">NOT SAVED</span>
                                  <span>{rowErrors[String(item.id)].message}</span>
                                </div>
                              )}
                            </td>
                            <td className="notes-cell" title={item.publicNotes || ''}>
                              {item.publicNotes || '-'}
                            </td>
                            <td className="tags-cell">
                              {(() => {
                                const tags = Array.isArray(item.tags) ? item.tags : (item.tags ? item.tags.split(' ').filter(Boolean) : []);
                                return tags.length > 0
                                  ? tags.map((tag, i) => {
                                      const color = tagColors[tag];
                                      return (
                                        <span
                                          key={i}
                                          className="tag-chip"
                                          style={color ? { backgroundColor: color, color: '#fff', borderColor: color } : {}}
                                        >
                                          {tag}
                                        </span>
                                      );
                                    })
                                  : '-';
                              })()}
                            </td>
                            <td className="updated-cell">
                              {formatDateTimeEastern(item.lastPriceUpdate, { year: undefined })}
                            </td>
                            <td>
                              {editingPrices[item.id] === undefined && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handlePriceChange(item.id, (item.bPrice || 0).toString())}
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {/* Pagination Controls - Bottom */}
            {inventory?.totalEvents > PAGE_SIZE && (
              <div className="pagination-controls" style={{ marginTop: '2rem', textAlign: 'center' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage + 1} of {Math.ceil(inventory.totalEvents / PAGE_SIZE)}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!inventory.hasMore}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
          </div>
        </div>

      </main>
    </div>
  );
}

export default Dashboard;
